import React, { useMemo, useState } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextAWS';

/**
 * CommissionerHub - command center listing every commissioner control in one
 * searchable place, grouped by area. Cards whose functionality already exists
 * in the app navigate to it; the rest are visible but tagged "Coming soon" so
 * the full control surface is discoverable while features land incrementally.
 */

// action: { type: 'nav', to: (leagueId) => path } navigates;
// omitted action = coming soon.
const SECTIONS = [
  {
    title: 'Setup & Roles',
    items: [
      { title: 'League Setup', desc: 'Edit league rules, scoring and settings.', action: { type: 'nav', to: (id) => `/league/${id}/settings` } },
      { title: 'Commissioner Messages', desc: 'Post messages to your league members.' },
      { title: 'Team Permissions', desc: 'Control what each team is allowed to do.' },
      { title: 'Team Administration', desc: 'Adjust fees, claims, budgets and caps per team.', action: { type: 'nav', to: (id) => `/league/${id}/settings` }, hint: 'Members & Teams tab' },
      { title: 'Edit Commissioners', desc: 'Enable or disable commissioner privileges for members, or reassign primary.' },
      { title: 'Replace Owner', desc: 'Hand a team over to a different owner.', action: { type: 'nav', to: (id) => `/league/${id}/settings` }, hint: 'Members & Teams tab' },
    ],
  },
  {
    title: 'Draft',
    items: [
      { title: 'Reset League & Rosters', desc: 'Clear all rosters and reset the league for a fresh draft.' },
      { title: 'Open Live Draft Room', desc: 'Enter the live draft room.', action: { type: 'nav', to: (id) => `/league/${id}/draft` } },
    ],
  },
  {
    title: 'Rosters & Players',
    items: [
      { title: 'Execute Keeper Rollover', desc: 'Drop non-kept players and roll over contracts.' },
      { title: 'Process Free Agent Claims', desc: 'Process all pending free agent claims now.' },
      { title: 'Process Waivers', desc: 'Run the waiver wire processing system.' },
      { title: 'Waive All Players', desc: 'Send all free agents to the waiver wire.' },
      { title: 'Illegal Roster Override', desc: 'Override roster legality per team and period.' },
      { title: 'Min/Max Violation Roster Override', desc: 'Override min/max roster constraint legality.' },
      { title: 'Position Eligibility Override (Full Season)', desc: 'Customize player position eligibility for the season.' },
      { title: 'Player Status Override', desc: 'Change the pool status of specific players.' },
      { title: 'Minors Eligibility Override', desc: 'Set the date a player became minors-ineligible.' },
    ],
  },
  {
    title: 'Stats & Scoring',
    items: [
      { title: 'Recalculate Fantasy Points', desc: 'Recalculate player fantasy point season totals.' },
      { title: 'Override Player Stats', desc: 'Change any stat for any player in any event.' },
      { title: 'Scoring/Standings Adjustment', desc: 'Apply manual scoring adjustments to the standings.' },
    ],
  },
  {
    title: 'Finances',
    items: [
      { title: 'Recalculate Free Agent Budgets', desc: "Recalculate each team's remaining free agent budget." },
      { title: 'Cap Hit Penalty Admin', desc: 'Manage salary cap hit penalties.' },
      { title: 'Salary/Contract Admin', desc: 'Edit player salaries and contracts.' },
      { title: 'Salary/Contract Converter', desc: 'Convert between salary and contract systems.' },
    ],
  },
  {
    title: 'League Management',
    items: [
      { title: 'Copy / Renew League', desc: 'Create a new league copied from this one, or renew it for the next season.' },
      { title: 'Delete This League', desc: 'Permanently remove this league from the system.', danger: true, action: { type: 'nav', to: (id) => `/league/${id}/settings` }, hint: 'General tab' },
    ],
  },
];

function CommissionerHub() {
  const { league } = useOutletContext();
  const { leagueId: routeLeagueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [query, setQuery] = useState('');
  const [treasurerNote, setTreasurerNote] = useState(false);

  const leagueId = league?.leagueId || league?.id || routeLeagueId;
  const userId = user?.userId || user?.id || user?.sub;
  const isCommissioner = league?.creatorId === userId;
  const seasonYear =
    (typeof league?.season === 'string' ? JSON.parse(league.season || '{}') : league?.season)?.year || 2026;

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => item.title.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q)
      ),
    })).filter((section) => section.items.length > 0);
  }, [query]);

  if (!isCommissioner) {
    return (
      <div className="comm-hub">
        <div className="comm-hub-guard">
          Commissioner tools are only available to this league's commissioner.
        </div>
      </div>
    );
  }

  const handleCard = (item) => {
    if (item.action?.type === 'nav' && leagueId) {
      navigate(item.action.to(leagueId));
    }
  };

  return (
    <div className="comm-hub">
      <h1 className="comm-hub-title">Commissioner Hub</h1>
      <div className="comm-hub-sub">
        {seasonYear} MLB · <span>{league?.name || 'League'}</span>
      </div>

      <div className="comm-hub-banner">
        <div className="comm-hub-banner-text">
          Enable the Treasurer to collect and manage league fees online
          {treasurerNote && <span className="comm-hub-banner-note"> — Treasurer tools are coming soon.</span>}
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setTreasurerNote(true)}>
          Enable Treasurer
        </button>
      </div>

      <input
        type="search"
        className="comm-hub-search"
        placeholder="Search commissioner actions…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search commissioner actions"
      />

      {filteredSections.length === 0 && (
        <div className="comm-hub-empty">No commissioner actions match "{query}".</div>
      )}

      {filteredSections.map((section) => (
        <div key={section.title} className="comm-hub-section">
          <h2 className="comm-hub-section-title">{section.title}</h2>
          <div className="comm-hub-cards">
            {section.items.map((item) => {
              const wired = item.action?.type === 'nav';
              return (
                <button
                  key={item.title}
                  type="button"
                  className={`comm-hub-card${item.danger ? ' danger' : ''}${wired ? '' : ' soon'}`}
                  onClick={() => handleCard(item)}
                  aria-disabled={!wired}
                >
                  <span className="comm-hub-card-head">
                    <span className="comm-hub-card-title">{item.title}</span>
                    {!wired && <span className="comm-hub-tag">Coming soon</span>}
                    {wired && item.hint && <span className="comm-hub-tag hint">{item.hint}</span>}
                  </span>
                  <span className="comm-hub-card-desc">{item.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default CommissionerHub;
